/**
 * Raw-fetch client for the OXBO Master Catalogs API (masters.dev.oxbo.energy).
 *
 * No masters SDK (MASTERS-PR3 deferred) — the SPA is a direct consumer, mirroring
 * adminApi.ts. Auth: same JWT bearer (tokenStore) + `credentials: "include"` for
 * the SSO cookie flow. masters uses the SAME admin_mode dual-gate as oxbo-auth,
 * so we send `X-OXBO-Admin-Mode: true` on every call (super_admin cross-org).
 *
 * Contract verified against https://masters.dev.oxbo.energy/openapi.json (v0.1.0).
 */
import { env } from "../config/env";
import { tokenStore } from "./tokenStore";

export type EntryScope = "stock" | "org";

// --- Categories ------------------------------------------------------------

export interface CategoryOut {
  category_key: string;
  display_name: string;
  /** Domain-family for grouped admin display; null → grouped under "Other". */
  category_group: string | null;
  property_schema: Record<string, unknown> | null;
  override_levels: string[];
  retired_at: string | null;
  created_at: string;
  created_by: string | null;
  modified_at: string;
  modified_by: string | null;
}

export interface CategoryCreate {
  category_key: string;
  display_name: string;
  category_group?: string | null;
  property_schema?: Record<string, unknown> | null;
  override_levels?: string[];
}

export interface CategoryUpdate {
  display_name?: string | null;
  /** Present → set (incl. null to clear); absent → unchanged. */
  category_group?: string | null;
  property_schema?: Record<string, unknown> | null;
  override_levels?: string[] | null;
  retired_at?: string | null;
}

// --- Entries (transport for PR2-b; gate-agnostic) --------------------------

export interface EntryOut {
  id: string;
  category_key: string;
  organization_id: string | null;
  site_region_key: string | null;
  entry_key: string;
  display_label: string;
  aliases: string[] | null;
  properties: Record<string, unknown> | null;
  is_active: boolean;
  superseded_by: string | null;
  created_at: string;
  created_by: string | null;
  modified_at: string;
  modified_by: string | null;
}

export interface EntryCreate {
  entry_key: string;
  display_label: string;
  scope?: EntryScope;
  site_region_key?: string | null;
  aliases?: string[] | null;
  properties?: Record<string, unknown> | null;
  is_active?: boolean;
  /** super_admin only — act on behalf of a specific org (scope:"org"). */
  admin_org_id?: string | null;
}

export interface EntryUpdate {
  display_label?: string | null;
  aliases?: string[] | null;
  properties?: Record<string, unknown> | null;
  is_active?: boolean | null;
  site_region_key?: string | null;
}

// --- Resolved catalog ------------------------------------------------------

export interface ResolvedCatalogOut {
  category_key: string;
  /** Catalog generation token (also echoed in the Catalog-Generation header). */
  generation: string;
  entries: EntryOut[];
}

export interface ResolvedCatalogResult {
  data: ResolvedCatalogOut;
  /** `Catalog-Generation` response header, when present. */
  generationHeader: string | null;
}

export interface ResolvedParams {
  /** Resolve for a specific site/region tier (optional). */
  site_region_key?: string;
}

// --- Audit -----------------------------------------------------------------

export interface AuditLogOut {
  ts: string;
  id: string;
  action: string;
  entity: string;
  entity_id: string | null;
  actor_user_id: string | null;
  organization_id: string | null;
  system_roles: string[] | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  source_app: string | null;
  x_request_id: string | null;
}

export interface ListAuditParams {
  organization_id?: string;
  limit?: number;
}

export class MastersApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(`MastersApiError ${status}`);
    this.name = "MastersApiError";
  }
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

async function rawFetch(method: string, path: string, body?: unknown): Promise<Response> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    // masters shares oxbo-auth's admin_mode dual-gate.
    "X-OXBO-Admin-Mode": "true",
  };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const token = tokenStore.getAccessToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  return fetch(`${env.mastersApiUrl}${path}`, {
    method,
    headers,
    credentials: "include",
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
}

async function parseBody(res: Response): Promise<unknown> {
  if (res.status === 204) return undefined;
  const ct = res.headers.get("Content-Type") ?? "";
  return ct.includes("application/json") ? res.json() : res.text();
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await rawFetch(method, path, body);
  const parsed = await parseBody(res);
  if (!res.ok) throw new MastersApiError(res.status, parsed);
  return parsed as T;
}

const cat = (key: string) => encodeURIComponent(key);

// --- Categories API --------------------------------------------------------

export function listCategories(): Promise<CategoryOut[]> {
  return request<CategoryOut[]>("GET", "/v1/categories");
}
export function getCategory(key: string): Promise<CategoryOut> {
  return request<CategoryOut>("GET", `/v1/categories/${cat(key)}`);
}
export function createCategory(body: CategoryCreate): Promise<CategoryOut> {
  return request<CategoryOut>("POST", "/v1/categories", body);
}
export function updateCategory(key: string, body: CategoryUpdate): Promise<CategoryOut> {
  return request<CategoryOut>("PUT", `/v1/categories/${cat(key)}`, body);
}
export function deleteCategory(key: string): Promise<void> {
  return request<void>("DELETE", `/v1/categories/${cat(key)}`);
}

// --- Entries API (used by PR2-b) -------------------------------------------

export function listEntries(categoryKey: string): Promise<EntryOut[]> {
  return request<EntryOut[]>("GET", `/v1/categories/${cat(categoryKey)}/entries`);
}
export function getEntry(categoryKey: string, entryId: string): Promise<EntryOut> {
  return request<EntryOut>(
    "GET",
    `/v1/categories/${cat(categoryKey)}/entries/${encodeURIComponent(entryId)}`,
  );
}
export function createEntry(categoryKey: string, body: EntryCreate): Promise<EntryOut> {
  return request<EntryOut>("POST", `/v1/categories/${cat(categoryKey)}/entries`, body);
}
export function updateEntry(
  categoryKey: string,
  entryId: string,
  body: EntryUpdate,
): Promise<EntryOut> {
  return request<EntryOut>(
    "PUT",
    `/v1/categories/${cat(categoryKey)}/entries/${encodeURIComponent(entryId)}`,
    body,
  );
}
export function deleteEntry(categoryKey: string, entryId: string): Promise<void> {
  return request<void>(
    "DELETE",
    `/v1/categories/${cat(categoryKey)}/entries/${encodeURIComponent(entryId)}`,
  );
}

// --- Resolved catalog API --------------------------------------------------

/**
 * GET /v1/categories/{key}/resolved?site_region_key= — effective merged catalog.
 *
 * NOTE: the endpoint takes NO target-org parameter; it resolves for the caller's
 * own org (from the token) + the optional site/region tier. Returns the body and
 * the `Catalog-Generation` response header (also echoed as `generation`).
 */
export async function getResolvedCatalog(
  categoryKey: string,
  params: ResolvedParams = {},
): Promise<ResolvedCatalogResult> {
  const res = await rawFetch(
    "GET",
    `/v1/categories/${cat(categoryKey)}/resolved${buildQuery({ ...params })}`,
  );
  const parsed = await parseBody(res);
  if (!res.ok) throw new MastersApiError(res.status, parsed);
  return {
    data: parsed as ResolvedCatalogOut,
    generationHeader: res.headers.get("Catalog-Generation"),
  };
}

// --- Audit API -------------------------------------------------------------

export function listAudit(params: ListAuditParams = {}): Promise<AuditLogOut[]> {
  return request<AuditLogOut[]>("GET", `/v1/admin/audit${buildQuery({ ...params })}`);
}
